import type { AnyAction, Middleware } from 'redux';
import { clearHistory, types as asrTypes } from '../../actions/backendAsr';
import {
    callNotification,
    changeCallMode,
    getNewSuggestions,
    resetCallMode,
    saveConversion,
    stopCallDetection,
    types as callTypes
} from '../../actions/backendCall';
import {
    getCallDurationMsec,
    getCallId,
    getLastCallId,
    getRemoveCall,
    getSessionDurationMsec,
    getUnfinishedCallDurationMsec,
    isCallDetectorInitialized,
    isCallHolded,
    isCallStarted
} from '../../selectors/call';
import {
    getAppConfig,
    getCallMetadataOnlyFlag,
    getCallTranscriptOnlyFlag,
    getDefaultCallMode,
    getEnableRecorderDebugFlag,
    getEnableVoiceEnergyDetectionFlag,
    getEnableRecorderFlag,
    getExplicitIntegrationSource,
    getIsTrainerEnabled,
    getRealtimeOnlyCallsPercent,
    getRecognitionForgetStreamFlag,
    isRealTimeCall
} from '../../selectors/config';
import { buildRecognitionConfig, getAsrConfig } from '../../selectors/asr';
import { getSafeSystemInfo, getAuthToken, getAuthUserData } from '../../selectors/auth';
import type { RootState } from '../../types';
import {
    CALL_NOTIFICATION_DURATION,
    CALL_NOTIFICATION_TIMOUT,
    CallMode,
    EXPLICIT_INTEGRATION_SOURCE,
    MAX_CALL_DETECTION_SESSION_DURATION_FOR_KEYPHRASE_DETECTION,
    SAMPLE_RATE_HERTZ,
    USER_EVENT_TYPE,
    wsConnectionClosingCodes
} from '../../../constants/backend';
import { proto } from '../../../proto/AsrProto';
import { reload, types as commonTypes } from '../../actions/backendCommon';
import { errorCodes } from '../../../utils/utils';
import { checkForBigerThanInt32 } from '../../../utils/background/general';
import { types as mainTypes } from '../../actions/backendMain';
import CallLifecycleController from '../../../services/CallLifecycleController';
import { types as configTypes, updateRecordCall } from '../../actions/backendConfig';
import {
    disableRecording,
    enableRecording,
    IS_ELECTRON_APP,
    IS_ELECTRON_APP_WITH_RECORDING,
    IS_LEGACY_LISTENER_MODE,
    IS_UI_ONLY_MODE
} from '../../../config/electron';
import { captureInfoMessage, captureWarnMessage } from '../../../tracker/raven';
import { isKeyphraseIntegrationMode } from '../../../utils/background/asr';
import { proto as listenerProto } from '../../../proto/ListenerProto';
import { types as deviceTypes } from '../../actions/backendDevice';
import { logUserEvent } from '../../actions/backendEventLogger';
import { dispatchBackend } from '../../types/backend';

const { CALL_DETECTION_STOPPED, RECOGNITION_CONNECTION_ERROR } = errorCodes.background;
const AUDIO_CHUNK_NOT_PROCESSED_WARN_THROTTLE_MSEC = 60 * 1000;

let lastAudioChunkNotProcessedWarningAt = 0;

// ----------------------

const callManagerMiddleware: Middleware = store => {
    const dispatch = (action: unknown) => dispatchBackend(store.dispatch, action);
    const getState = store.getState as () => RootState;
    const controller = new CallLifecycleController({ dispatch });

    return next => action => {
        const typedAction = action as AnyAction;
        const result = next(action);
        const { type, payload = {} } = typedAction || {};
        const state = getState();

        function startCallDetection() {
            if (!getIsTrainerEnabled(state)) {
                // if sales only mode
                console.info('TRAINER DISABLED');
                return;
            }

            controller.startDetection(
                {
                    // sample rate is guarantied by listener
                    sampleRateHertz: SAMPLE_RATE_HERTZ,
                    systemInfo: getSafeSystemInfo(state),
                    userData: getAuthUserData(state),
                    jwt: getAuthToken(state),
                    ...getAsrConfig(state),
                    ...getAppConfig(state)
                },
                buildRecognitionConfig(state)
            );
        }

        function endCall({ reason, code = null }: { reason: number; code?: number | null }) {
            const sessionDurationMsec = getSessionDurationMsec(state);
            const callDurationMsec = getCallDurationMsec(state);

            const realTimeCall = isRealTimeCall(state);
            const isMetadataOnlyCall = getCallMetadataOnlyFlag(state);
            const isTranscriptOnlyCall = getCallTranscriptOnlyFlag(state);
            const isRecognitionForgetStream = getRecognitionForgetStreamFlag(state);

            if (realTimeCall) {
                controller.setMode(CallMode.META);
            } else if (isRecognitionForgetStream || getRemoveCall(state) || isMetadataOnlyCall) {
                controller.setMode(CallMode.NONE);
            } else if (isTranscriptOnlyCall) {
                controller.forgetAudio();
            }

            controller.stopDetection({
                acdSessionOffsetMsec: checkForBigerThanInt32(sessionDurationMsec) ? 0 : sessionDurationMsec,
                asrSessionOffsetMsec: checkForBigerThanInt32(callDurationMsec) ? 0 : callDurationMsec,
                reason,
                code: code ?? undefined
            });
        }

        switch (type) {
            case callTypes.START_CALL_DETECTION: {
                // Logic flow:
                // getUserProfile - set isTrainerActive -> getAppConfig -> startCallDetection
                if (!state.backend.main.isTrainerActive) {
                    return;
                }

                startCallDetection();
                break;
            }
            case callTypes.CALL_STARTED: {
                const realtimeOnlyCallsPercent = getRealtimeOnlyCallsPercent(state);
                dispatch(updateRecordCall(realtimeOnlyCallsPercent));

                controller.onCallStarted(buildRecognitionConfig(state));

                dispatch(logUserEvent(USER_EVENT_TYPE.CALL_START, getCallId(state), payload.eventMeta));

                break;
            }

            case callTypes.CALL_HOLDED: {
                controller.onHolded();
                break;
            }

            case callTypes.ASR_PING: {
                if (isCallHolded(state)) {
                    controller.sendPing();
                }
                break;
            }

            case deviceTypes.AUDIO_CHUNK_RECEIVED: {
                const {
                    audio,
                    offsetInAudioStreamMsec,
                    channel //  DataFlow.CAPTURE=1,DataFlow.RENDER=2
                } = payload.audioChunk;

                if (![listenerProto.DataFlow.CAPTURE, listenerProto.DataFlow.RENDER].includes(channel)) {
                    break;
                }

                const canProcessAudioChunk = isCallDetectorInitialized(state) && !isCallHolded(state);

                if (canProcessAudioChunk) {
                    controller.processAudioChunk({
                        audioChunk: audio,
                        offsetInAudioStreamMsec,
                        channel
                    });
                } else if (isCallStarted(state) && !isCallDetectorInitialized(state)) {
                    const now = Date.now();
                    if (now - lastAudioChunkNotProcessedWarningAt > AUDIO_CHUNK_NOT_PROCESSED_WARN_THROTTLE_MSEC) {
                        lastAudioChunkNotProcessedWarningAt = now;
                        captureWarnMessage('audio chunk received but not processed by call manager', {
                            source: 'CallManager.audioDiagnostics',
                            callId: getCallId(state),
                            isCallStarted: isCallStarted(state),
                            isCallDetectorInitialized: isCallDetectorInitialized(state),
                            isCallHolded: isCallHolded(state),
                            channel,
                            offsetInAudioStreamMsec
                        });
                    }
                }

                const callLenMscec = getUnfinishedCallDurationMsec(state);
                const appConfig = getAppConfig(state);
                if (appConfig && isKeyphraseIntegrationMode(appConfig) && callLenMscec && controller.isCallActive()) {
                    if (callLenMscec > MAX_CALL_DETECTION_SESSION_DURATION_FOR_KEYPHRASE_DETECTION) {
                        dispatch(stopCallDetection(proto.FinalizationReason.CALL_END_TIMEOUT));
                    }

                    const timeOutNotificationMsec =
                        MAX_CALL_DETECTION_SESSION_DURATION_FOR_KEYPHRASE_DETECTION - CALL_NOTIFICATION_DURATION;
                    if (callLenMscec > timeOutNotificationMsec) {
                        dispatch(callNotification(CALL_NOTIFICATION_TIMOUT));
                    }
                }
                break;
            }

            case callTypes.CALL_DETECTION_STOPPED: {
                const { code = null } = payload;

                if (code === wsConnectionClosingCodes.NO_RELOAD) return;

                if (isCallStarted(state) && IS_UI_ONLY_MODE) {
                    dispatch({
                        type: callTypes.CALL_END,
                        payload: {
                            eventMeta: { source: 'call_detection_stopped' }
                        }
                    });
                    break;
                }

                dispatch(
                    reload({
                        code: CALL_DETECTION_STOPPED,
                        reason: 'CALL_DETECTION_STOPPED'
                    })
                );

                break;
            }

            case callTypes.CALL_ENDED: {
                dispatch(clearHistory());

                controller.onCallEnded();

                endCall({
                    reason: proto.FinalizationReason.CALL_END_NORMAL,
                    code: wsConnectionClosingCodes.NO_RELOAD
                });

                dispatch(resetCallMode());
                startCallDetection();
                controller.scheduleReloadOnIdle();

                // @todo add points here (-> WordsDetection._getDetectionCount, but it counts only words, not points)
                dispatch(logUserEvent(USER_EVENT_TYPE.CALL_END, getLastCallId(state), payload.eventMeta));

                break;
            }

            case asrTypes.ASR_VENDOR_CONFIG_ERROR: {
                dispatch(
                    reload({
                        isImmediate: true,
                        code: errorCodes.background.ASR_VENDOR_CONFIG_ERROR,
                        reason: 'ASR_VENDOR_CONFIG_ERROR'
                    })
                );
                break;
            }

            case commonTypes.RELOAD: {
                dispatch(stopCallDetection(proto.FinalizationReason.CLIENT_FORCE_RELOAD));
                break;
            }
            case commonTypes.CLOSE_ALL_CONNECTIONS:
            case callTypes.STOP_CALL_DETECTION: {
                const { reason = proto.FinalizationReason.CLIENT_FORCE_RELOAD, code = null } = payload;
                endCall({ reason, code });
                break;
            }
            case asrTypes.ASR_CONNECTION_CLOSED: {
                if ((IS_LEGACY_LISTENER_MODE || IS_ELECTRON_APP_WITH_RECORDING) && !payload.closedByClient) {
                    dispatch(
                        reload({
                            code: RECOGNITION_CONNECTION_ERROR,
                            reason: 'RECOGNITION_CONNECTION_ERROR'
                        })
                    );
                }
                break;
            }
            case mainTypes.EVENT_FORWARDED: {
                const { jsonRequest } = payload;
                controller.forwardEvent({
                    jsonRequest: JSON.stringify(jsonRequest),
                    offsetMsec: getSessionDurationMsec(state)
                });
                break;
            }
            case callTypes.CALL_START: {
                if (controller.isCallActive()) {
                    const callStartContext = {
                        source: 'CallManager.CALL_START',
                        currentCallID: getCallId(state),
                        lastCallID: getLastCallId(state),
                        sessionDurationMsec: getSessionDurationMsec(state),
                        payloadMetaCallID: payload?.meta?.call_id || null,
                        eventMeta: payload?.eventMeta || null,
                        isCallActive: controller.isCallActive()
                    };
                    captureWarnMessage('previous call is not ended', callStartContext);
                    captureInfoMessage('dispatching CALL_RESTART because CALL_START arrived during active call', callStartContext);

                    dispatch({
                        type: callTypes.CALL_RESTART,
                        payload
                    });

                    break;
                }

                dispatch(resetCallMode());

                controller.emitCallStart({
                    sessionOffsetMsec: getSessionDurationMsec(state),
                    meta: payload.meta,
                    eventMeta: payload.eventMeta
                });
                break;
            }
            case callTypes.CALL_END: {
                controller.emitCallEnd({
                    reason: proto.FinalizationReason.CALL_END_NORMAL,
                    sessionOffsetMsec: getSessionDurationMsec(state),
                    eventMeta: payload.eventMeta
                });
                break;
            }
            case callTypes.CALL_HOLD: {
                controller.hold();
                dispatch(logUserEvent(USER_EVENT_TYPE.CALL_HOLD));
                break;
            }
            case callTypes.CALL_UNHOLD: {
                controller.unhold();
                dispatch(logUserEvent(USER_EVENT_TYPE.CALL_UNHOLD));
                break;
            }

            // manual opt-in/out events
            case callTypes.OPT_IN: {
                controller.setMode(CallMode.BOTH);
                break;
            }
            case callTypes.OPT_STOP: {
                controller.setMode(CallMode.AGENT, true, false);
                break;
            }
            case callTypes.OPT_OUT: {
                controller.setMode(CallMode.AGENT, true, true);
                break;
            }

            case callTypes.CALL_SET_MODE: {
                const resolvedMode = typeof payload.resolvedMode === 'number' ? payload.resolvedMode : null;
                const mode = payload.mode === CallMode.DEFAULT ? (resolvedMode ?? getDefaultCallMode(state)) : payload.mode;

                controller.setMode(mode, true, !!payload.forgetAudioAndTranscript);

                if (mode === CallMode.NONE) {
                    controller.emitCallEnd({
                        reason: proto.FinalizationReason.CALL_END_NORMAL,
                        sessionOffsetMsec: getSessionDurationMsec(state)
                    });
                }
                break;
            }

            case callTypes.RESET_CALL_MODE: {
                controller.resetMode();
                break;
            }

            case callTypes.UTC_PING: {
                controller.utcPing();
                break;
            }

            case callTypes.UTC_PING_TIMEOUT: {
                controller.emitCallEnd({
                    reason: proto.FinalizationReason.SESSION_RELOAD_ON_LOST,
                    sessionOffsetMsec: getSessionDurationMsec(state)
                });
                break;
            }

            case callTypes.SEND_MANUAL_CALL_EVENT: {
                const defaultCallMode = getDefaultCallMode(state);
                if ([CallMode.UNKNOWN, CallMode.NONE].indexOf(defaultCallMode) > -1) {
                    dispatch(
                        reload({
                            code: errorCodes.background.CALL_MODE_ERROR,
                            reason: 'CALL_MODE_ERROR'
                        })
                    );
                    break;
                }

                controller.forwardEvent({
                    jsonRequest: JSON.stringify({
                        type: payload.isStart ? 'start' : 'end',
                        source: 'i2x_manual'
                    }),
                    offsetMsec: getSessionDurationMsec(state)
                });

                setTimeout(() => {
                    // hack for redux
                    if (payload.isStart) {
                        dispatch(resetCallMode());
                        controller.emitCallStart({
                            sessionOffsetMsec: getSessionDurationMsec(state),
                            eventMeta: {
                                source: 'manual'
                            }
                        });
                        dispatch(changeCallMode(CallMode.DEFAULT));
                    } else {
                        controller.emitCallEnd({
                            reason: proto.FinalizationReason.CALL_END_NORMAL,
                            sessionOffsetMsec: getSessionDurationMsec(state),
                            eventMeta: {
                                source: 'manual'
                            }
                        });
                    }
                }, 1);
                break;
            }

            // On every call started we need to recalculate if the call is going to be
            // recorded or not based on the configuration we got.
            case configTypes.UPDATE_APP_CONFIG_SUCCESS:
            case configTypes.GET_APP_CONFIG_SUCCESS: {
                const realtimeOnlyCallsPercent = getRealtimeOnlyCallsPercent(state);
                dispatch(updateRecordCall(realtimeOnlyCallsPercent));

                // enable/disable electron recording & reload if local storage flag changed
                const enableRecorder = getEnableRecorderFlag(state);
                if (enableRecorder ? enableRecording() : disableRecording()) {
                    dispatch(
                        reload({
                            reason: 'SETTINGS_UPDATED',
                            isImmediate: true
                        })
                    );
                }

                if (IS_ELECTRON_APP) {
                    const explicitIntegrationSource = getExplicitIntegrationSource(state);
                    if (
                        explicitIntegrationSource != null &&
                        [
                            EXPLICIT_INTEGRATION_SOURCE.I2X_CALL_EVENT,
                            EXPLICIT_INTEGRATION_SOURCE.UCT_CALL_EVENT,
                            EXPLICIT_INTEGRATION_SOURCE.COPS_I2X_EVENT
                        ].includes(explicitIntegrationSource) &&
                        window.electronAPI
                    ) {
                        window.electronAPI.startCallEventsServer?.();
                    }

                    // debug recorder
                    controller.setRecorderDebugFlag(getEnableRecorderDebugFlag(state));
                    // standalone voice-energy activity logging (backend-gated, independent of keyphrase)
                    controller.setVoiceEnergyDetectionFlag(getEnableVoiceEnergyDetectionFlag(state));
                }

                break;
            }

            case callTypes.SAVE_CONVERSION: {
                dispatch(
                    saveConversion({
                        ...payload
                    })
                );

                break;
            }

            case callTypes.GET_NEW_SUGGESTIONS: {
                dispatch(getNewSuggestions());

                break;
            }

            default:
        }

        return result;
    };
};

export default callManagerMiddleware;
